/**
 * Models - Shared
 */
const GadgetsOrderClient = (model: IGadgetsOrderClient): IGadgetsOrderClientModel => {
	const { id, uuid, email, email_change, phone_number } = model

	return {
		id,
		uuid,
		email,
		emailChange: email_change,
		phoneNumber: phone_number,
	}
}

const GadgetsOrderClientProfile = (model: IGadgetsOrderClientProfile): IGadgetsOrderClientProfileModel => {
	const { client_id, first_name, last_name } = model

	return {
		clientId: client_id,
		firstName: first_name,
		lastName: last_name,
	}
}

const GadgetsOrderDeliveryAddress = (model: IGadgetsOrderDeliveryAddress): IGadgetsOrderDeliveryAddressModel => {
	const {
		name,
		city,
		street,
		postcode,
		country_code,
		staircase,
		apartment_number,
		building_number,
		delivery_phone_number,
		courier_instructions,
	} = model

	return {
		name,
		city,
		street,
		postcode,
		staircase,
		countryCode: country_code,
		apartmentNumber: apartment_number,
		buildingNumber: building_number,
		deliveryPhoneNumber: delivery_phone_number,
		courierInstructions: courier_instructions,
	}
}

/**
 * Models
 */
export const GadgetsOrder = (model: IGadgetsOrder): IGadgetsOrderModel => {
	const {
		order_id,
		order_date,
		is_shipment_label,
		shipment_status,
		shipment_source,
		shipment_location,
		delivery_method,
		delivery_target,
		delivery_address,
		client,
		client_profile,
		...rest
	} = model

	return {
		...rest,
		orderId: order_id,
		orderDate: order_date ? new Date(order_date) : null,
		isShipmentLabel: is_shipment_label,
		shipmentStatus: shipment_status,
		shipmentSource: shipment_source,
		shipmentLocation: shipment_location,
		deliveryMethod: delivery_method,
		deliveryTarget: delivery_target,
		deliveryAddress: delivery_address ? GadgetsOrderDeliveryAddress(delivery_address) : null,
		client: client ? GadgetsOrderClient(client) : null,
		clientProfile: client_profile ? GadgetsOrderClientProfile(client_profile) : null,
	}
}

/**
 * Models - Details - Items
 */
const GadgetsOrderDetailsItemWarehouse = (
	model: IGadgetsOrderDetailsItemWarehouse,
): IGadgetsOrderDetailsItemWarehouseModel => {
	const { ...rest } = model

	return {
		...rest,
	}
}

const GadgetsOrderDetailsItemGadget = (model: IGadgetsOrderDetailsItemGadget): IGadgetsOrderDetailsItemGadgetModel => {
	const { main_image, ...rest } = model

	return {
		...rest,
		mainImage: main_image ? Media(main_image) : null,
	}
}

const GadgetsOrderDetailsItemVariant = (
	model: IGadgetsOrderDetailsItemVariant,
): IGadgetsOrderDetailsItemVariantModel => {
	const { attributes, image, ...rest } = model

	return {
		...rest,
		attributes: attributes.map(GadgetsProductVariantAttribute),
		image: image ? Media(image) : null,
	}
}

const GadgetsOrderDetailsItem = (model: IGadgetsOrderDetailsItem): IGadgetsOrderDetailsItemModel => {
	const { id, uuid, quantity, points_total, points_price_snapshot, gadget, variant, warehouse } = model

	return {
		id,
		uuid,
		quantity,
		pointsTotal: points_total,
		pointsPriceSnapshot: points_price_snapshot,
		variant: variant ? GadgetsOrderDetailsItemVariant(variant) : null,
		gadget: GadgetsOrderDetailsItemGadget(gadget),
		warehouse: GadgetsOrderDetailsItemWarehouse(warehouse),
	}
}

/**
 * Models - Details
 */
const GadgetsOrderDetailsOrder = (model: IGadgetsOrderDetailsOrder): IGadgetsOrderDetailsOrderModel => {
	const {
		id,
		uuid,
		order_id,
		order_date,
		order_datetime,
		shipment_status,
		shipment_source,
		shipment_location,
		delivery_method,
		delivery_target,
		points_total,
	} = model

	return {
		id,
		uuid,
		orderId: order_id,
		orderDate: order_date ? new Date(order_date) : null,
		orderDatetime: order_datetime ? new Date(order_datetime) : null,
		shipmentStatus: shipment_status,
		shipmentSource: shipment_source,
		shipmentLocation: shipment_location,
		deliveryMethod: delivery_method,
		deliveryTarget: delivery_target,
		pointsTotal: points_total,
	}
}

const GadgetsOrderDetailsParcelLocker = (model: IGadgetsOrderParcelLocker): IGadgetsOrderParcelLockerModel => {
	const { id, name, email, address, phone_number, recipient_name } = model

	return {
		id,
		name,
		email,
		address,
		phoneNumber: phone_number,
		recipientName: recipient_name,
	}
}

const GadgetsOrderDetailsShipment = (model: IGadgetsOrderDetailsShipment): IGadgetsOrderDetailsShipmentModel => {
	const {
		id,
		uuid,
		status,
		provider,
		provider_reference,
		tracking_number,
		shipped_at,
		delivered_at,
		has_label,
		labels,
	} = model

	return {
		id,
		uuid,
		status,
		labels,
		provider,
		providerReference: provider_reference,
		trackingNumber: tracking_number,
		shippedAt: shipped_at,
		deliveredAt: delivered_at,
		hasLabel: has_label,
	}
}

export const GadgetsOrderDetails = (json: IGadgetsOrderDetails): IGadgetsOrderDetailsModel => {
	const {
		order,
		client,
		client_profile,
		delivery_address,
		parcel_locker,
		items,
		items_count_total,
		shipment,
		...rest
	} = json

	return {
		...rest,
		client: client ? GadgetsOrderClient(client) : null,
		clientProfile: client_profile ? GadgetsOrderClientProfile(client_profile) : null,
		deliveryAddress: delivery_address ? GadgetsOrderDeliveryAddress(delivery_address) : null,
		items: items ? items.map(GadgetsOrderDetailsItem) : [],
		itemsCountTotal: items_count_total,
		parcelLocker: parcel_locker ? GadgetsOrderDetailsParcelLocker(parcel_locker) : null,
		shipment: shipment ? GadgetsOrderDetailsShipment(shipment) : null,
		order: GadgetsOrderDetailsOrder(order),
	}
}

/**
 * Models - Shipments
 */
const GadgetsOrderShipmentItem = (model: IGadgetOrderShipmentItem): IGadgetOrderShipmentItemModel => {
	const { provider_reference, tracking_number, has_label, ...rest } = model

	return {
		...rest,
		providerReference: provider_reference,
		trackingNumber: tracking_number,
		hasLabel: has_label,
	}
}

export const GadgetsOrderShipment = (model: IGadgetOrderShipment): IGadgetOrderShipmentModel => {
	const { shipment, status } = model

	return {
		status,
		shipment: shipment ? GadgetsOrderShipmentItem(shipment) : null,
	}
}

/**
 * Models - Filters
 */
const GadgetsOrderFilterDateRange = (model: IGadgetsOrderFilterDateRange): IGadgetsOrderFilterDateRangeModel => {
	const { from, to } = model

	return {
		maximumOrderDateRange: from ? new Date(from) : null,
		minimumOrderDateRange: to ? new Date(to) : null,
	}
}

const GadgetsOrderFilterWarehouse = (model: IGadgetsOrderFilterWarehouse): IGadgetsOrderFilterWarehouseModel => {
	const { id, uuid, name, is_external_supplier, partner_id } = model

	return {
		id,
		uuid,
		name,
		partnerId: partner_id,
		isExternalSupplier: is_external_supplier,
	}
}

export const GadgetsOrderFilters = (model: IGadgetsOrderFilters): IGadgetsOrderFiltersModel => {
	const { warehouses, order_date_range, shipment_sources, shipment_statuses, delivery_methods } = model

	return {
		orderDateRange: GadgetsOrderFilterDateRange(order_date_range),
		shipmentSources: shipment_sources,
		shipmentStatuses: shipment_statuses,
		deliveryMethods: delivery_methods,
		warehouses: warehouses ? warehouses.map(GadgetsOrderFilterWarehouse) : [],
	}
}

/**
 * Payloads
 */
export const GadgetsOrdersShipmentStatusBulkToPayload = (
	ids: number[] | string[],
	status: EGadgetsOrdersShipmentStatus,
): IGadgetsOrdersShipmentStatusBulkPayload => {
	const payloadIds: number[] = ids.map(Number).filter((id): id is number => id !== undefined && !Number.isNaN(id))

	return {
		order_ids: payloadIds,
		status,
	}
}
